import { useTranslation } from 'react-i18next'

import type { EmailNotificationFrequency } from '@/api/types'
import { PageBar } from '@/app/layout/page-bar'
import { useUpdateAccount } from '@/features/account/queries'
import { useSessionStore } from '@/features/auth/session-store'
import { DatabaseTokens } from '@/features/database/tokens/components/database-tokens'
import { useUiStore, type Theme } from '@/shared/stores/ui-store'
import { Label } from '@/shared/ui/label'
import { NativeSelect } from '@/shared/ui/native-select'

/**
 * The account's own settings.
 *
 * Notifications, appearance, and the database tokens the account owns. Identity
 * belongs to the identity provider -- the name and address arrive in the sign-in
 * claims and are refreshed from them on every sign-in -- and so does existence:
 * the realm creates accounts and the realm removes them, so there is nothing
 * here to close.
 *
 * A route rather than a dialog, because it is reached from the user menu rather
 * than from anything on screen.
 */
export function SettingsPage() {
  const { t } = useTranslation()

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PageBar />
      {/* Full width, so the scrollbar sits at the page's edge rather than the column's. */}
      <div className="min-h-0 flex-1 overflow-auto">
        <div className="mx-auto w-full max-w-2xl space-y-8 p-6">
          <header>
            <h1 className="text-xl font-semibold">{t('settings.title')}</h1>
          </header>

          <NotificationSection />
          <AppearanceSection />

          <Section
            title={t('databaseToken.title')}
            description={t('databaseToken.description')}
          >
            <DatabaseTokens />
          </Section>
        </div>
      </div>
    </div>
  )
}

function Section({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <section className="space-y-4 rounded-lg border p-4 bg-background on-surface">
      <div>
        <h2 className="font-medium">{title}</h2>
        {description !== undefined && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      {children}
    </section>
  )
}

const FREQUENCIES: readonly EmailNotificationFrequency[] = [
  'instant',
  'daily',
  'weekly',
  'never',
]

function NotificationSection() {
  const { t } = useTranslation()
  const frequency = useSessionStore(
    (state) => state.user?.email_notification_frequency ?? 'instant',
  )
  const update = useUpdateAccount()

  return (
    <Section title={t('settings.notifications')}>
      <div className="space-y-2">
        <Label htmlFor="settings-frequency">{t('settings.emailFrequency')}</Label>
        <NativeSelect
          id="settings-frequency"
          value={frequency}
          disabled={update.isPending}
          // Saved on change rather than behind a button: a select with one value
          // and no draft state has nothing to submit.
          onChange={(event) => {
            update.mutate({
              email_notification_frequency: event.target
                .value as EmailNotificationFrequency,
            })
          }}
        >
          {FREQUENCIES.map((option) => (
            <option key={option} value={option}>
              {t(`settings.frequency.${option}` as 'settings.frequency.instant')}
            </option>
          ))}
        </NativeSelect>
      </div>
    </Section>
  )
}

const THEMES: readonly Theme[] = ['system', 'light', 'dark']

function AppearanceSection() {
  const { t } = useTranslation()
  const theme = useUiStore((state) => state.theme)
  const setTheme = useUiStore((state) => state.setTheme)

  return (
    <Section title={t('settings.appearance')}>
      <div className="space-y-2">
        <Label htmlFor="settings-theme">{t('settings.theme')}</Label>
        <NativeSelect
          id="settings-theme"
          value={theme}
          onChange={(event) => setTheme(event.target.value as Theme)}
        >
          {THEMES.map((option) => (
            <option key={option} value={option}>
              {t(`settings.themes.${option}` as 'settings.themes.system')}
            </option>
          ))}
        </NativeSelect>
      </div>
    </Section>
  )
}
